import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDateTime, relativeTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Site = Schema<'app__v1__seo__SiteOut'>
type Monitor = Schema<'MonitorOut'>
type Check = Schema<'CheckOut'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * The intervals `MonitorIn` admits, as the three §6 names.
 *
 * 300–900 seconds is a *range* on the server, not an enumeration, so a value
 * outside these three is legal and `optionsFor` adds it rather than silently
 * showing the wrong one selected — a support script or a `psql` session can put
 * 450 on a row, and a control that then read as *Every 5 minutes* would be
 * lying about what is happening to the tenant's site.
 */
const INTERVALS = [300, 600, 900] as const

/** How the monitor's three-way configuration is spelled in the one control. */
const OFF = 'off'
const PAUSED = 'paused'

/**
 * Why the difference between the two "stopped" options is in the caption under
 * the table rather than in the option text.
 *
 * *Off* and *Paused* both stop the pings and they are not the same thing —
 * `DELETE` discards the monitor's history and its interval, `PUT enabled:
 * false` keeps both — so the distinction has to be stated somewhere. It was in
 * the options first, as *Off — forget this monitor*, and a real browser showed
 * why that is the wrong place: a `<select>` is as wide as its widest option, so
 * two explanatory labels pushed the control past the edge of a seven-column
 * table and clipped it. The sentence moved to where there is room for it, and
 * the options went back to one word each.
 *
 * The same measurement took the prototype's *Response* column out and put the
 * latency under *Last check* as a caption: seven columns did not fit the shell's
 * content width, and the one that got pushed past the edge was the control. A
 * row's latency and the time it was measured are one fact anyway.
 */

/**
 * Sites — `design/screens/seo.html#sites`, audit row §A3, built in S-G6's
 * successor over `api/app/v1/uptime.py`.
 *
 * S-B30 shipped the pinger, the state machine and all three tenant-facing
 * routes on 2026-08-16 and the sidebar has rendered *Sites* disabled ever
 * since. This is the screen those routes were built for, and it is the last of
 * `navigation.ts`'s stale entries.
 *
 * WHY THE MOCKUP'S ONE INTERVAL SELECT BECAME ONE PER ROW
 * --------------------------------------------------------
 * The prototype draws a single *Check interval* select in the uptime card's
 * footer, which reads as an organisation-wide setting. It is not one:
 * `uptime_monitors.interval_seconds` is per monitor and a monitor is per site,
 * for the same reason `run_schedules.site_id` is unique — an agency running a
 * staging domain beside a client's live domain wants different answers. So the
 * control moved into the row it actually governs, and it is the *only* control
 * on this screen because the three routes are one decision at three settings:
 * `DELETE` is *not watched*, `PUT enabled: false` is *paused*, and `PUT` with an
 * interval is *watched this often*. A separate Start/Stop pair beside a select
 * would let the tenant press *Stop* on a monitor whose interval they had just
 * changed and wonder which of the two had happened.
 *
 * **Paused is offered rather than folded into *not watched***, although both
 * stop the pings. Deleting the monitor discards its history and its interval;
 * pausing keeps both, which is what somebody doing a migration at midnight
 * wants — and `test_a_paused_monitor_is_not_swept` says the server means it.
 *
 * WHY THE COLUMN SAYS "LAST 24 HOURS" AND NOT "30-DAY UPTIME"
 * ------------------------------------------------------------
 * Because a 30-day figure does not exist. `UptimeTransaction.history` returns
 * `HISTORY_LIMIT` rows — 288, one day at the fastest interval — and nothing
 * aggregates beyond that window, so the prototype's *99.97%* would have to be
 * computed from a month AugRank does not keep. This is the same call §V2 made
 * about the prototype's ₹4.20: the honest answer is the window that exists,
 * labelled as that window, rather than a plausible number with nothing behind
 * it. The caption under the table says so in as many words.
 *
 * WHAT IS DELIBERATELY NOT ON THIS SCREEN
 * -----------------------------------------
 * **Add site.** The prototype's *Add site — 1 left on Growth* implies a per-plan
 * site quota, and there is none in the model: `plans` meters runs and seats, not
 * sites. The only writer of a site is `PUT /api/v1/onboarding/site`, which also
 * settles wizard step 1 and takes a business description and an approval-gating
 * choice — a form the prototype does not draw. So the empty state sends a tenant
 * to the wizard, which is where a site is added today, and the quota sentence is
 * not invented.
 *
 * **Run audit now, and the per-site Settings link.** Both already exist, on
 * `/seo/audit` and `/seo/triggers`, over the endpoints that own them. A second
 * button that posts the same run from here would be a second place to look when
 * it goes wrong.
 *
 * **The monitor's URL as an editable field.** `MonitorIn.url` is required and
 * this screen derives it — `https://{domain}`, the same origin
 * `app/seo/sitemap.origin_of` and `app/seo/publish.py` both build — and an
 * existing monitor keeps whatever URL it already has. A box here would let a row
 * headed *kavyainteriors.in* report a different site's health, which is the one
 * thing an uptime table must never do.
 */
export function Sites(): JSX.Element {
  const { role } = useActiveTenant()
  // `discovery:manage` is what `app/v1/uptime.py` puts on `PUT` and `DELETE`,
  // on the line it draws in its own comment: operating the product rather than
  // purchasing it. Reading is `tenant:read`, which every seat holds.
  const manageable = may(role, 'discovery:manage')

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  const listed = sites.state.data.sites

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Sites</h1>
        <span className="app-spacer" />
        <span className="ar-subtle ar-text-body-sm">
          {listed.length === 1 ? '1 site' : `${String(listed.length)} sites`}
        </span>
      </div>

      {listed.length === 0 ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <Empty
              title="No site yet"
              body="AugRank has nothing to audit, rank or watch until it knows where your pages live. Adding your website is the first step of setup — finish it and this page fills in."
              action={
                <a className="ar-btn ar-btn--primary ar-btn--sm" href="/onboarding/site">
                  Add your website
                </a>
              }
            />
          </div>
        </div>
      ) : (
        <>
          <div className="ar-grid ar-grid--2">
            {listed.map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </div>
          <UptimeCard sites={listed} manageable={manageable} />
        </>
      )}
    </div>
  )
}

/* --- one site ------------------------------------------------------------------- */

/** What each `verification_status` means, in the tenant's words. */
const VERIFICATION: Record<string, { tone: 'success' | 'warning' | 'danger'; label: string }> = {
  verified: { tone: 'success', label: '✓ Verified' },
  no_access: { tone: 'danger', label: '⚠ No access' },
  unverified: { tone: 'warning', label: '⚠ Not verified' },
}

/** The sentence under the table, when there is something to explain. */
const VERIFICATION_NOTE: Record<string, string> = {
  no_access:
    'The connected Google account has no Search Console property for this domain. Connect a different account, or ask whoever manages the site to give you access.',
  unverified:
    'AugRank has not matched this domain to a Search Console property yet. Until it does, rank and search figures for this site stay empty — uptime below does not need it.',
}

/**
 * One site, and what Google has been able to say about it.
 *
 * Facts only, and no query of its own: everything here arrives in the one
 * `GET /seo/sites` the page already made. The uptime figures are the table
 * below rather than a line in each card, because the question they answer —
 * *which of my sites is down* — is a comparison, and a comparison spread across
 * cards is one the reader has to do themselves.
 */
function SiteCard({ site }: { site: Site }): JSX.Element {
  const badge = VERIFICATION[site.verification_status] ?? {
    tone: 'warning' as const,
    label: site.verification_status,
  }
  const note = VERIFICATION_NOTE[site.verification_status]

  return (
    <section className="ar-card" aria-label={site.domain}>
      <div className="ar-card__header">
        <h3>{site.domain}</h3>
        <span className="ar-card__spacer" />
        <span className={`ar-badge ar-badge--${badge.tone}`}>{badge.label}</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        <table className="ar-table app-sites__facts">
          <tbody>
            <tr>
              <td>Search Console property</td>
              <td className="ar-num ar-mono">{site.gsc_property ?? '—'}</td>
            </tr>
            <tr>
              <td>Analytics property</td>
              <td className="ar-num ar-mono">
                {site.ga4_property_id === null || site.ga4_property_id === undefined
                  ? '—'
                  : site.ga4_property_id}
              </td>
            </tr>
            <tr>
              <td>Verified</td>
              <td className="ar-num ar-muted">
                {site.verified_at ? formatDateTime(site.verified_at, IST) : 'Not yet'}
              </td>
            </tr>
          </tbody>
        </table>
        {note ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {note}
          </p>
        ) : null}
      </div>
    </section>
  )
}

/* --- uptime --------------------------------------------------------------------- */

/**
 * Every site's uptime, one row each.
 *
 * The copy in the body and the footer is the prototype's, with one correction
 * it could not have known about: it says *"two consecutive failures email
 * you"*, and `app/seo/uptime.py` fixed the threshold at **one** because S-B30's
 * exit criterion is detection *within one ping interval* and two failures would
 * make that two intervals. The recovery is symmetric for the same reason, and
 * the notification carries a per-transition dedupe key so a flapping site sends
 * one notice per episode rather than one per ping. Saying "two" here would have
 * a tenant waiting for a second failure that is never counted.
 */
function UptimeCard({ sites, manageable }: { sites: Site[]; manageable: boolean }): JSX.Element {
  return (
    <section className="ar-card" aria-label="Uptime">
      <div className="ar-card__header">
        <h3>Uptime</h3>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--neutral">Checked from outside AWS</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
          These checks are deliberately dumb — a plain request from outside AWS on a fixed interval,
          with no model in the loop. A monitor that can reason about whether your site is down is a
          monitor that can talk itself out of paging you.
        </p>
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Status</th>
                <th className="ar-num">Last 24 h</th>
                <th className="ar-num">Last check</th>
                <th>Last incident</th>
                <th>
                  <span className="ar-visually-hidden">Checks</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <UptimeRow key={site.id} site={site} manageable={manageable} />
              ))}
            </tbody>
          </table>
        </div>
        <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
          <strong>Last 24 h</strong> is exactly that. AugRank keeps the last 288 checks for each
          site — one day at the fastest interval — so there is no 30-day figure to show.{' '}
          <strong>Off</strong> removes a site’s monitor and the checks it has collected;{' '}
          <strong>Paused</strong> keeps both and stops fetching until you pick an interval again.
        </p>
      </div>
      <div className="ar-card__footer">
        <span className="ar-subtle ar-text-body-sm">
          One failed check takes a site down and emails you; one success brings it back. We never
          open a support ticket on your behalf, and we never change your site because it was down.
        </span>
      </div>
    </section>
  )
}

/**
 * One site's row, and the control that configures it.
 *
 * Its own query rather than one request for the whole table, because there is
 * no endpoint for the whole table: `GET /seo/sites/{id}/uptime` is per site and
 * a monitor is per site. One component per row is what makes that N requests
 * fired together instead of N awaited in sequence, and what lets a row that
 * fails say so without taking the other rows' figures off the screen.
 */
function UptimeRow({ site, manageable }: { site: Site; manageable: boolean }): JSX.Element {
  const toast = useToast()
  const now = new Date()
  const [failure, setFailure] = useState<string | null>(null)

  const uptime = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/uptime', {
          params: { path: { site_id: site.id } },
          signal,
        }),
      ),
    [site.id],
  )

  const save = useMutation((body: Schema<'MonitorIn'>) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: site.id } },
        body,
      }),
    ),
  )

  const stop = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: site.id } },
      }),
    ),
  )

  const domain = <td className="ar-mono app-sites__domain">{site.domain}</td>

  if (uptime.state.status === 'loading') {
    return (
      <tr>
        {domain}
        <td className="ar-muted" colSpan={5} aria-busy="true">
          Reading this site’s checks…
        </td>
      </tr>
    )
  }

  if (uptime.state.status === 'error') {
    return (
      <tr>
        {domain}
        <td colSpan={5}>
          <span className="app-sites__error" role="alert">
            {uptime.state.failure.message}
          </span>{' '}
          <Button size="sm" onClick={uptime.refetch}>
            Try again
          </Button>
        </td>
      </tr>
    )
  }

  const monitor = uptime.state.data.monitor
  const history = uptime.state.data.history ?? []
  const busy = save.pending || stop.pending

  /**
   * Apply the one control's new value.
   *
   * The three branches are the three routes, and the refetch afterwards is what
   * makes the row describe the row rather than the request: `PUT` answers with
   * the monitor, but `DELETE` answers 204 and the *Last 24 hours* column has to
   * come from somewhere either way.
   */
  const choose = (value: string): void => {
    setFailure(null)
    void (async () => {
      if (value === OFF) {
        const result = await stop.run(undefined)
        if (!result.ok) {
          setFailure(result.failure.message)
          return
        }
        uptime.refetch()
        toast.show({
          kind: 'success',
          title: `Stopped watching ${site.domain}`,
          body: 'Its checks and their history have been discarded.',
        })
        return
      }

      const paused = value === PAUSED
      const seconds = paused ? (monitor?.interval_seconds ?? INTERVALS[0]) : Number(value)
      const result = await save.run({
        url: monitor?.url ?? `https://${site.domain}`,
        enabled: !paused,
        interval_seconds: seconds,
      })
      if (!result.ok) {
        setFailure(result.failure.message)
        return
      }
      uptime.refetch()
      toast.show({
        kind: 'success',
        title: paused
          ? `Paused the checks on ${site.domain}`
          : `Checking ${site.domain} ${describeInterval(seconds).toLowerCase()}`,
        body: paused
          ? 'Nothing is being fetched. Its history and interval are kept.'
          : 'AugRank will email you the first time a check does not answer.',
      })
    })()
  }

  const control = (
    <td>
      <select
        className="ar-select app-sites__interval"
        aria-label={`Uptime checks for ${site.domain}`}
        value={valueOf(monitor)}
        disabled={!manageable || busy}
        onChange={(event) => {
          choose(event.target.value)
        }}
      >
        <option value={OFF}>Off</option>
        <option value={PAUSED}>Paused</option>
        {optionsFor(monitor).map((seconds) => (
          <option key={seconds} value={String(seconds)}>
            {describeInterval(seconds)}
          </option>
        ))}
      </select>
      {failure ? (
        <div className="app-sites__error" role="alert">
          {failure}
        </div>
      ) : null}
    </td>
  )

  if (monitor === null) {
    return (
      <tr>
        {domain}
        <td>
          <span className="ar-badge ar-badge--neutral">Not watched</span>
        </td>
        <td className="ar-muted" colSpan={3}>
          AugRank is not checking this site.
        </td>
        {control}
      </tr>
    )
  }

  const share = uptimeShare(history)

  return (
    <tr>
      {domain}
      <td>
        <StatusBadge monitor={monitor} />
      </td>
      <td className="ar-num">
        {share === null ? (
          <span className="ar-muted">No checks yet</span>
        ) : (
          <>
            {formatShare(share)}
            <div className="app-sites__meta">
              {history.length === 1 ? '1 check' : `${String(history.length)} checks`}
            </div>
          </>
        )}
      </td>
      <td className="ar-num ar-muted">
        {monitor.last_checked_at === null ? 'Never' : relativeTime(monitor.last_checked_at, now)}
        {monitor.last_latency_ms === null ? null : (
          <div className="app-sites__meta">{monitor.last_latency_ms} ms</div>
        )}
      </td>
      <td className="ar-muted">{describeIncident(history)}</td>
      {control}
    </tr>
  )
}

/**
 * Up, down, never checked, or paused — in that order of precedence.
 *
 * Paused wins over the state it was paused in, because a green dot beside a
 * monitor nobody is running says the site is up on evidence that stopped
 * arriving at an unknown point. `unknown` is a third state rather than an
 * optimistic green for the same reason `app/seo/uptime.State` makes it one.
 */
function StatusBadge({ monitor }: { monitor: Monitor }): JSX.Element {
  if (!monitor.enabled) return <span className="ar-badge ar-badge--neutral">Paused</span>
  if (monitor.state === 'up') return <span className="ar-badge ar-badge--success">● Up</span>
  if (monitor.state === 'down') {
    return (
      <>
        <span className="ar-badge ar-badge--danger">● Down</span>
        {monitor.down_for_seconds === null ? null : (
          <div className="app-sites__meta">for {describeOutage(monitor.down_for_seconds)}</div>
        )}
      </>
    )
  }
  return <span className="ar-badge ar-badge--neutral">Not checked yet</span>
}

/* --- the small conversions ------------------------------------------------------ */

/** Which option the control shows for the monitor in hand. */
function valueOf(monitor: Monitor | null): string {
  if (monitor === null) return OFF
  if (!monitor.enabled) return PAUSED
  return String(monitor.interval_seconds)
}

/**
 * The intervals to offer, including one the server holds that this list does
 * not — see `INTERVALS` on why a fourth is possible and must not be hidden.
 */
function optionsFor(monitor: Monitor | null): number[] {
  const stored = monitor?.interval_seconds
  if (stored === undefined || INTERVALS.includes(stored as (typeof INTERVALS)[number])) {
    return [...INTERVALS]
  }
  return [...INTERVALS, stored].sort((a, b) => a - b)
}

/**
 * An interval, as short as it can be said.
 *
 * *Every 15 min* rather than *Every 15 minutes*, because a `<select>` is as wide
 * as its widest option and this one sits in the last column of a table that has
 * to fit the shell's content width. Measured in a real browser at 1185px: the
 * long form put the control past the edge of its own card.
 */
function describeInterval(seconds: number): string {
  return `Every ${String(Math.round(seconds / 60))} min`
}

/** How long a site has been down, in the coarsest unit that is still true. */
function describeOutage(seconds: number): string {
  if (seconds < 90) return 'under a minute'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${String(minutes)} minutes`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'}`
  const days = Math.round(hours / 24)
  return `${String(days)} day${days === 1 ? '' : 's'}`
}

/** The share of the window's checks that answered, or `null` for no window. */
function uptimeShare(history: Check[]): number | null {
  if (history.length === 0) return null
  return (history.filter((check) => check.ok).length * 100) / history.length
}

/**
 * A percentage with one decimal, and none when it is whole.
 *
 * `99.7%` rather than `99.65517%`: the finest distinction 288 samples can draw
 * is a third of a percent, so further digits are precision the data does not
 * have. A clean `100%` keeps no decimal because `100.0%` reads as rounded.
 */
function formatShare(share: number): string {
  return Number.isInteger(share) ? `${String(share)}%` : `${share.toFixed(1)}%`
}

/**
 * The most recent failed check in the window, or the absence of one.
 *
 * Read from `history` rather than from `last_changed_at`, which is when the
 * *state* last flipped — that is the start of the current outage when a site is
 * down and the end of the last one when it is up, and neither is the thing a
 * column headed *Last incident* is asked for.
 */
function describeIncident(history: Check[]): string {
  const failed = history.find((check) => !check.ok)
  if (failed === undefined) return 'None in the last day'
  const when = formatDateTime(failed.checked_at, IST)
  const why =
    failed.error ??
    (failed.status_code === null ? 'no answer' : `${String(failed.status_code)} from origin`)
  return `${when} · ${why}`
}
